export type ChallengeVideoRecord = {
  id: string;
  videoId: string;
  youtubeUrl: string;
  thumbnailUrl?: string;
  title?: string;
  likeCount?: number;
  createdAt?: string | number | null;
  updatedAt?: string | number | null;
};

export const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

export function parseYouTubeVideoId(url: string): string | null {
  const raw = String(url || '').trim();
  if (!raw) return null;

  try {
    const parsed = new URL(raw);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    let candidate = '';

    if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
      if (parsed.pathname === '/watch') {
        candidate = parsed.searchParams.get('v') || '';
      } else if (parsed.pathname.startsWith('/embed/')) {
        candidate = parsed.pathname.split('/').filter(Boolean)[1] || '';
      } else if (parsed.pathname.startsWith('/shorts/')) {
        candidate = parsed.pathname.split('/').filter(Boolean)[1] || '';
      }
    } else if (hostname === 'youtu.be') {
      candidate = parsed.pathname.split('/').filter(Boolean)[0] || '';
    }

    const normalized = String(candidate || '').trim();
    return YOUTUBE_ID_PATTERN.test(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

export function getYouTubeThumbnailUrl(videoId: string, quality: 'hq' | 'mq' = 'hq'): string {
  const id = String(videoId || '').trim();
  if (!YOUTUBE_ID_PATTERN.test(id)) return '';
  return `https://img.youtube.com/vi/${id}/${quality === 'mq' ? 'mqdefault' : 'hqdefault'}.jpg`;
}

export function getYouTubeEmbedUrl(videoId: string): string {
  const id = String(videoId || '').trim();
  if (!YOUTUBE_ID_PATTERN.test(id)) return '';
  return `https://www.youtube.com/embed/${id}`;
}

export function getYouTubeAutoplayEmbedUrl(videoId: string): string {
  const id = String(videoId || '').trim();
  if (!YOUTUBE_ID_PATTERN.test(id)) return '';
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
}

export function getYouTubeWatchUrl(videoId: string): string {
  const id = String(videoId || '').trim();
  if (!YOUTUBE_ID_PATTERN.test(id)) return '';
  return `https://www.youtube.com/watch?v=${id}`;
}

export function normalizeChallengeVideoRecord(raw: any): ChallengeVideoRecord | null {
  const videoId = String(raw?.videoId || raw?.video_id || '').trim();
  if (!YOUTUBE_ID_PATTERN.test(videoId)) return null;

  const youtubeUrl = String(raw?.youtubeUrl || raw?.youtube_url || '').trim() || getYouTubeWatchUrl(videoId);
  const createdAt = raw?.createdAt ?? raw?.created_at ?? null;
  const updatedAt = raw?.updatedAt ?? raw?.updated_at ?? null;
  const rawLikeCount = raw?.likeCount ?? raw?.like_count ?? raw?.likes ?? raw?.like_count_total ?? 0;
  const likeCount = Number(rawLikeCount);

  return {
    id: String(raw?.id || '').trim(),
    videoId,
    youtubeUrl: parseYouTubeVideoId(youtubeUrl) === videoId ? youtubeUrl : getYouTubeWatchUrl(videoId),
    thumbnailUrl: getYouTubeThumbnailUrl(videoId),
    title: String(raw?.title || '').trim() || undefined,
    likeCount: Number.isFinite(likeCount) && likeCount > 0 ? Math.floor(likeCount) : 0,
    createdAt,
    updatedAt,
  };
}

export function formatChallengeVideoDate(value: string | number | null | undefined): string {
  if (value == null || value === '') return 'Recently added';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently added';
  return date.toLocaleDateString();
}
